/**
 * Author: Romain Hapi Sr.
 * Version: 2.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Page d'accueil du site Tchad.
 *
 * Elle enchaine hero, annonce FaDel, services, projets, temoignages et appel a
 * l'action. Projets et temoignages viennent de `mockData` : ils sont stables
 * et n'ont pas de route backend.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useReveal } from '../hooks/useReveal';
import SEO from '../../components/SEO';
import { SectionHead } from '../components/Section';
import { ProjectCard } from '../components/ProjectCard';
import { FadelLaunch } from '../components/FadelLaunch';
import { SERVICES, LIFT } from '../serviceCatalog';
import { mockProjects, mockTestimonials } from '../mockData';
import {
  SHELL,
  SECTION,
  EYEBROW_LIGHT,
  H2,
  H1,
  H3,
  LEAD,
  CARD_DARK,
  BTN_PRIMARY,
  BTN_ON_DARK,
  BTN_ON_LIGHT,
  RULE,
} from '../theme';

const META = {
  fr: {
    title: 'Prenim Tchad - Transformation numerique et infrastructure IT',
    description:
      "Conseil, developpement et infrastructure IT a N'Djamena et a travers l'Afrique. Une equipe adossee a Prenim UK.",
  },
  en: {
    title: 'Prenim Tchad - Digital transformation and IT infrastructure',
    description:
      "Consulting, development and IT infrastructure in N'Djamena and across Africa. A team backed by Prenim UK.",
  },
  ar: {
    title: 'Prenim Tchad',
    description:
      'الاستشارات والتطوير والبنية التحتية لتقنية المعلومات في نجامينا وعبر أفريقيا.',
  },
};

const EYEBROW = {
  fr: "Depuis 2021 · Royaume-Uni & N'Djamena",
  en: "Since 2021 · United Kingdom & N'Djamena",
  ar: 'منذ 2021 · المملكة المتحدة ونجامينا',
};

const LABELS = {
  fr: {
    services: 'Nos expertises',
    proof: 'References',
    words: 'Ils nous font confiance',
    all: 'Voir tout',
  },
  en: {
    services: 'Our expertise',
    proof: 'Selected work',
    words: 'Client words',
    all: 'View all',
  },
  ar: {
    services: 'خبراتنا',
    proof: 'أعمال مختارة',
    words: 'آراء العملاء',
    all: 'عرض الكل',
  },
};

export const Home = () => {
  const { t, language } = useLanguage();
  // Contenu local : `/api/projects` et `/api/testimonials` n'ont jamais
  // existe cote backend (404 en production). Les appels ne servaient qu'a
  // retomber ici apres une requete perdue.
  const projects = mockProjects;
  const testimonials = mockTestimonials;

  const servicesReveal = useReveal();
  const projectsReveal = useReveal();
  const wordsReveal = useReveal();
  const ctaReveal = useReveal();

  const meta = META[language] || META.fr;
  const labels = LABELS[language] || LABELS.fr;

  const [featured, ...others] = projects.slice(0, 3);

  const stats = [
    { value: '50+', label: { fr: 'Projets realises', en: 'Projects completed', ar: 'مشروع منجز' } },
    { value: '15+', label: { fr: 'Pays africains', en: 'African countries', ar: 'دولة أفريقية' } },
    {
      value: '4+',
      label: { fr: "Annees d'activite", en: 'Years of activity', ar: 'سنوات من النشاط' },
    },
    { value: '100%', label: { fr: 'Satisfaction client', en: 'Client satisfaction', ar: 'رضا العملاء' } },
  ];

  return (
    <main data-testid="tchad-home-page">
      <SEO title={meta.title} description={meta.description} lang={language} />

      {/* ---------------------------------------------------------------- HERO */}
      <section
        data-testid="tchad-home-hero"
        className="relative flex min-h-[92vh] items-center overflow-hidden bg-slate-950 text-white"
      >
        <img
          src="/images/hero-tchad-bureau.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Voile dense a gauche, plus clair a droite : le texte reste lisible
            sans effacer entierement la photographie. */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/75 to-slate-950/35" />
        <div className="absolute inset-0 bg-tchad-weave opacity-60" />

        {/* La barre de statistiques est en `absolute bottom-0` : sans reserve
            basse, elle recouvrait le second bouton sur mobile, ou elle occupe
            deux rangees au lieu d'une. */}
        <div className={`relative z-10 w-full ${SHELL} pt-32 pb-56 sm:pb-44 lg:pt-40 lg:pb-40`}>
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-xs font-medium text-amber-300 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
              {EYEBROW[language] || EYEBROW.fr}
            </span>

            <h1 className={`${H1} mt-6`}>
              {t.hero.title}
              <span className="mt-2 block text-amber-400">{t.hero.subtitle}</span>
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300 lg:text-xl">
              {t.hero.description}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/tchad/services" data-testid="tchad-hero-cta-primary" className={BTN_PRIMARY}>
                {t.hero.cta}
                <ArrowRight className="h-4 w-4 rtl-flip" />
              </Link>
              <Link
                to="/tchad/contact"
                data-testid="tchad-hero-cta-secondary"
                className={BTN_ON_DARK}
              >
                {t.hero.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>

        {/* Barre de statistiques ancree en bas du hero, comme sur le site UK. */}
        <div className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10 bg-slate-950/70 backdrop-blur-md">
          <div className={`${SHELL} grid grid-cols-2 gap-6 py-6 md:grid-cols-4`}>
            {stats.map((stat, index) => (
              <div key={index} data-testid={`tchad-hero-stat-${index}`} className="flex flex-col">
                <span className="text-3xl font-bold tracking-tight text-white md:text-4xl">
                  {stat.value}
                </span>
                <span className="mt-1 text-xs uppercase tracking-wider text-slate-400">
                  {stat.label[language]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- LANCEMENT FADEL */}
      <FadelLaunch />

      {/* ------------------------------------------------------------ SERVICES */}
      <section data-testid="tchad-home-services" className={`${SECTION} bg-white`}>
        <div className={SHELL}>
          {/* Grille asymetrique 5/7 : l'intitule a gauche, le contenu a droite.
              Le tout-centre precedent donnait sept sections identiques. */}
          <div className="grid gap-12 lg:grid-cols-12">
            {/* La colonne de gauche reste collee au defilement : elle tenait
                sur trois lignes face a six cartes, laissant un grand vide. */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <SectionHead
                  eyebrow={labels.services}
                  title={t.services.title}
                  lead={t.services.subtitle}
                />
                <Link
                  to="/tchad/services"
                  data-testid="tchad-services-all"
                  className={`${BTN_ON_LIGHT} mt-8`}
                >
                  {labels.all}
                  <ArrowRight className="h-4 w-4 rtl-flip" />
                </Link>
              </div>
            </div>

            <div ref={servicesReveal.ref} className={`${servicesReveal.className} lg:col-span-7`}>
              {/* Cartes separees, et non plus soudees par une grille a filets
                  d'1px : le decollage au survol y serait rogne par le
                  conteneur, et laisserait un trou dans la trame. */}
              <div className="grid gap-5 sm:grid-cols-2">
                {SERVICES.map(({ key, slug, Icon, bg, hover, icon, rule }) => {
                  const service = t.services[key];
                  if (!service) return null;
                  return (
                    <Link
                      key={key}
                      to={`/tchad/services/${slug}`}
                      data-testid={`tchad-service-${key}`}
                      className={`group relative z-0 flex flex-col overflow-hidden rounded-lg border border-slate-200/80 p-8 ${LIFT} ${bg} ${hover}`}
                    >
                      {/* Filet de couleur en tete de carte : il porte la teinte
                          sans la laisser envahir le texte, qui doit rester
                          lisible sur fond clair. */}
                      <span
                        aria-hidden="true"
                        className={`absolute inset-x-0 top-0 h-1 ${rule}`}
                      />
                      <Icon className={`h-6 w-6 ${icon}`} strokeWidth={1.5} />
                      <h3 className={`${H3} mt-5 text-slate-950`}>{service.name}</h3>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-700">
                        {service.description}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-950 opacity-0 transition-opacity group-hover:opacity-100">
                        {t.projects.viewDetails}
                        <ArrowRight className="h-3.5 w-3.5 rtl-flip" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- PROJETS */}
      <section data-testid="tchad-home-projects" className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead
              eyebrow={labels.proof}
              title={t.projects.title}
              lead={t.projects.subtitle}
            />
            <Link to="/tchad/projects" className={`${BTN_ON_LIGHT} shrink-0`}>
              {labels.all}
              <ArrowRight className="h-4 w-4 rtl-flip" />
            </Link>
          </div>

          {/* La realisation phare occupe une bande pleine largeur, les deux
              suivantes se partagent la ligne du dessous.

              Une colonne de 7 face a une colonne de 5 semblait plus riche,
              mais la carte phare a moins de contenu que deux cartes empilees :
              l'ecart de hauteur se payait soit en dalle noire, soit en vide
              blanc, soit en demi-colonne vide. Sur une bande, chaque carte
              prend sa hauteur naturelle et la hierarchie tient a la largeur. */}
          <div ref={projectsReveal.ref} className={`${projectsReveal.className} mt-16 space-y-8`}>
            {featured ? (
              <ProjectCard
                project={featured}
                language={language}
                viewLabel={t.projects.viewDetails}
                featured
              />
            ) : null}

            <div className="grid gap-8 md:grid-cols-2">
              {others.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  language={language}
                  viewLabel={t.projects.viewDetails}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- TEMOIGNAGES */}
      <section
        data-testid="tchad-home-testimonials"
        className={`${SECTION} relative overflow-hidden bg-slate-950 text-white`}
      >
        <div className="absolute inset-0 bg-tchad-weave" />
        <div className={`relative ${SHELL}`}>
          <SectionHead
            eyebrow={labels.words}
            title={t.testimonials.title}
            lead={t.testimonials.subtitle}
            dark
          />

          <div
            ref={wordsReveal.ref}
            className={`${wordsReveal.className} mt-16 grid gap-8 md:grid-cols-3`}
          >
            {testimonials.map((testimonial) => (
              <figure
                key={testimonial.id}
                data-testid={`tchad-testimonial-${testimonial.id}`}
                className={`${CARD_DARK} flex flex-col p-8`}
              >
                <Quote className="h-7 w-7 shrink-0 text-amber-400/70" strokeWidth={1.5} />
                <blockquote className="mt-5 flex-1 text-base leading-relaxed text-slate-300">
                  {testimonial.content[language]}
                </blockquote>
                <figcaption className="mt-8 border-t border-white/10 pt-5">
                  <div className="flex gap-0.5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <div className="mt-3 font-semibold text-white">{testimonial.name}</div>
                  <div className="text-sm text-slate-400">{testimonial.position[language]}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------- CTA FINAL */}
      <section data-testid="tchad-home-cta" className={`${SECTION} bg-white`}>
        <div className={SHELL}>
          <div
            ref={ctaReveal.ref}
            className={`${ctaReveal.className} grid items-center gap-10 rounded-lg border border-slate-200 bg-slate-50 p-10 lg:grid-cols-12 lg:p-16`}
          >
            <div className="lg:col-span-7">
              <div className={RULE} />
              <h2 className={`${H2} mt-5 text-slate-950`}>{t.contact.title}</h2>
              <p className={`mt-5 max-w-xl ${LEAD}`}>{t.contact.subtitle}</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:col-span-5 lg:justify-end">
              <Link to="/tchad/contact" data-testid="tchad-cta-contact" className={BTN_PRIMARY}>
                {t.hero.ctaSecondary}
                <ArrowRight className="h-4 w-4 rtl-flip" />
              </Link>
              <Link to="/tchad/careers" data-testid="tchad-cta-careers" className={BTN_ON_LIGHT}>
                {t.nav.careers}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
